import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLocale } from '@aviv-unified-frontend/runtime/i18n';
import { Box, useBreakpointValue } from '@gemini/core';
import { Accordion, Button, Divider } from '@gemini/ui';
import { MediumCta } from '@/entities/enums/MediumCta';
import { formatCurrency } from '@/helpers/format/formatCurrency';
import { getMediumAttribute } from '@/helpers/tracking/tracking';
import { usePageLayoutContext } from '@/hooks/usePageLayoutContext/usePageLayoutContext';
import { useShellTranslation } from '@/hooks/useShellTranslation/useShellTranslation';
import { isServer } from '@/utils/isServer';
import { Link } from '../Link';
export const PlaceLinkBox = ({ titleI18nKey, introI18nKey, links = [], labelI18nKey, getLabelValue, columnCount = 1, placeName, withPrice, cheapestAndMostExpensive, buttonLink, boldCurrentPlace = false, }) => {
    const { language } = useLocale();
    const { t } = useShellTranslation();
    const { pricePageProps: { pricePageContext }, } = usePageLayoutContext();
    const title = t(titleI18nKey, {
        replace: { place: placeName || '' },
        unsafe_escapeValue: false,
    });
    const intro = introI18nKey
        ? t(introI18nKey, {
            replace: { place: placeName || '' },
            unsafe_escapeValue: false,
        })
        : '';
    const columnNumber = useBreakpointValue({
        base: 1,
        md: 2,
        lg: 1,
        xl: 2,
    });
    if (!links.length &&
        !cheapestAndMostExpensive?.cheapest.links?.length &&
        !cheapestAndMostExpensive?.mostExpensive.links?.length) {
        return null;
    }
    const placeLinks = (links, withPrice, subtitle, intro, marginTop) => (_jsxs(_Fragment, { children: [withPrice && (_jsxs(_Fragment, { children: [subtitle && (_jsx(Box, { typography: "typography.body.14.bold", marginBottom: "spacing.16", marginTop: marginTop ? 'spacing.32' : '', children: t(subtitle) })), intro && (_jsx(Box, { typography: "typography.body.16.regular", marginBottom: "spacing.16", marginTop: marginTop ? 'spacing.32' : '', children: intro })), _jsxs(Box, { typography: "typography.body.14.bold", display: "flex", justifyContent: "space-between", paddingVertical: "spacing.8", children: [_jsx(Box, { children: t(withPrice.titlePlaceI18nKey) }), _jsx(Box, { children: t('link_boxes.sheet_title_prices') })] }), _jsx(Divider, {})] })), links.map((item, index) => {
                const label = getLabelValue ? getLabelValue(item) : item.placeName;
                const linkLabelI18nKey = item.labelI18nKey || labelI18nKey;
                const shouldRenderLink = (!isServer() && window?.isUser) || !item.obfuscated;
                const linkContent = linkLabelI18nKey
                    ? t(linkLabelI18nKey, { replace: { place: label } })
                    : label;
                const mediumAttribute = !item.withoutMediumParam
                    ? getMediumAttribute(MediumCta.LINK_BOXES, pricePageContext.place.type, item.placeType)
                    : undefined;
                const shouldBoldCurrentPlace = boldCurrentPlace && item.placeName === pricePageContext.place.name.full;
                return (_jsxs(Box, { padding: "spacing.4", display: "flex", justifyContent: "space-between", children: [shouldRenderLink ? (_jsx(Box, { as: "span", typography: shouldBoldCurrentPlace
                                ? 'typography.body.16.bold'
                                : 'typography.body.16.regular', children: _jsx(Link, { mediumAttribute: mediumAttribute, href: item.url, isExternal: item.isExternal, children: linkContent }) })) : (_jsx(Box, { as: "span", typography: shouldBoldCurrentPlace
                                ? 'typography.body.16.bold'
                                : 'typography.body.16.regular', children: linkContent })), item.price && (_jsx(Box, { as: "p", marginLeft: "spacing.8", marginVertical: "unset", color: "color.content.light.default", typography: shouldBoldCurrentPlace
                                ? 'typography.body.16.bold'
                                : 'typography.body.16.regular', children: formatCurrency(item.price, language) }))] }, index));
            }), buttonLink?.url && buttonLink?.textKey && (_jsx(Box, { textAlign: "left", paddingVertical: "spacing.16", children: _jsx(Button, { variant: "secondary", href: buttonLink.url, children: t(buttonLink.textKey) }) }))] }));
    const content = (_jsx(Box, { unsafe_style: {
            columnCount: cheapestAndMostExpensive ? columnNumber : columnCount,
            columnGap: 'spacing.16',
        }, children: cheapestAndMostExpensive ? (_jsxs(_Fragment, { children: [cheapestAndMostExpensive.mostExpensive.links &&
                    placeLinks(cheapestAndMostExpensive.mostExpensive.links.sort((a, b) => {
                        const priceA = a.price ?? 0;
                        const priceB = b.price ?? 0;
                        return priceB - priceA;
                    }), cheapestAndMostExpensive.mostExpensive.withPrice, 'link_boxes.multiple_levels.most_expensive_streets.title'), cheapestAndMostExpensive.cheapest.links &&
                    placeLinks(cheapestAndMostExpensive.cheapest.links.sort((a, b) => {
                        const priceA = a.price ?? 0;
                        const priceB = b.price ?? 0;
                        return priceA - priceB;
                    }), cheapestAndMostExpensive.cheapest.withPrice, 'link_boxes.multiple_levels.cheapest_streets.title', undefined, true)] })) : (placeLinks(links, withPrice, undefined, intro)) }));
    return (_jsx(Box, { maxWidth: "100%", children: _jsx(Accordion, { variant: "divided", content: content, marginVertical: "spacing.none", 
            // @ts-ignore
            title: _jsx(Box, { as: "h2", typography: "typography.body.16.bold", margin: "0", children: title }) }, title) }));
};
